// printDoc.js — render an HTML fragment to a print/Save-as-PDF window.
// Same mechanism the app already uses for VO proformas and diaries.

import { REPORT_NOTICE } from "../legal";

// A fixed footer that repeats on every printed page (and shows on the last
// page of the saved PDF). Stamps ownership + confidentiality on every export.
const FOOTER_CSS =
  ".sf-doc-footer{position:fixed;bottom:6mm;left:0;right:0;text-align:center;" +
  "font:9px/1.3 Arial,Helvetica,sans-serif;color:#64748b;letter-spacing:.02em;" +
  "border-top:1px solid #e2e8f0;padding-top:3px}";

const footerHtml =
  '<div class="sf-doc-footer">' +
  REPORT_NOTICE.replace(/&/g, "&amp;").replace(/</g, "&lt;") +
  "</div>";

const openPrintUrls = new Set();
let cleanupRegistered = false;

function registerPrintUrl(url) {
  openPrintUrls.add(url);
  if (cleanupRegistered || typeof window === "undefined" || !window.addEventListener) return;
  cleanupRegistered = true;
  window.addEventListener("pagehide", () => {
    openPrintUrls.forEach((openUrl) => URL.revokeObjectURL(openUrl));
    openPrintUrls.clear();
  });
}

// Open a complete HTML document in a new window/tab via a Blob URL.
// Replaces the old window.open("") + document.write pattern, which newer
// browsers render as a permanently blank about:blank tab. autoPrint injects
// an on-load window.print() so the dialog opens once the document (and any
// images) has finished loading.
export function openHtmlWindow(html, { features, autoPrint = false } = {}) {
  let doc = html;
  if (autoPrint) {
    const printScript =
      "<script>window.addEventListener('load',function(){setTimeout(function(){window.print();},150);});</script>";
    doc = doc.includes("</body>")
      ? doc.replace("</body>", printScript + "</body>")
      : doc + printScript;
  }
  const url = URL.createObjectURL(new Blob([doc], { type: "text/html" }));
  const win = features ? window.open(url, "_blank", features) : window.open(url, "_blank");
  if (!win) {
    URL.revokeObjectURL(url);
    alert("Pop-up blocked — allow pop-ups for this site to generate the PDF.");
    return null;
  }
  win.focus();
  // Keep Blob URLs alive while the app page is open. Some browsers re-read the
  // document URL when window.print() starts; revoking on a timer can make the
  // visible print tab turn into a blank page or blank PDF preview.
  registerPrintUrl(url);
  return win;
}

export function printDocHtml(innerHtml, title = "Document") {
  const css =
    "@page{size:A4;margin:16mm}body{margin:0;padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact}" +
    FOOTER_CSS;
  openHtmlWindow(
    '<!doctype html><html><head><meta charset="utf-8"><title>' + title + "</title>" +
    "<style>" + css + "</style></head><body>" + innerHtml + footerHtml + "</body></html>",
    { features: "width=900,height=1100", autoPrint: true }
  );
}

export function downloadHtml(innerHtml, filename = "document.html") {
  const blob = new Blob(
    ['<!doctype html><html><head><meta charset="utf-8"><style>' + FOOTER_CSS +
      '</style></head><body>' + innerHtml + footerHtml + "</body></html>"],
    { type: "text/html" }
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
